import { GridColDef } from "@mui/x-data-grid-pro";
import { Button, IconButton } from "@mui/material";
import { SubcontaDropdown } from "../../../../domain/models/subconta_dropdown";
import { CustomAutocomplete } from "../../../components/custom_autocomplete";
import { CustomInput } from "../../../components/custom_input";
import { CustomInputArea } from "../../../components/custom_input_area";
import { CustomMoneyMaskedInput } from "../../../components/custom_input_money_mask";

import DynamicIcon from "../../../../../../common/components/dynamic_icon";

/**
 * GenerateFolhaPagamentoCadastroColumnsDef
 */

type Props = {
  subcontas: SubcontaDropdown[],
  onActionChangeTitulo: (idItemFolhaPagamento: number, valor: string) => void;
  onActionChangeSubconta: (idItemFolhaPagamento: number, idSubconta: number | null) => void;
  onActionChangeFormaPagamento: (idItemFolhaPagamento: number, valor: string) => void;
  onActionChangeValor: (idItemFolhaPagamento: number, valor: number | null) => void;
  onActionChangeInfoGerais: (idItemFolhaPagamento: number, valor: string) => void;
  onActionViewItem: (idItemFolhaPagamento: number, openNewTab: boolean) => void;
  onActionDelete: (idItemFolhaPagamento: number, openState: boolean) => void;
};

export const GenerateFolhaPagamentoCadastroColumnsDef = ({
  subcontas,
  onActionChangeTitulo,
  onActionChangeSubconta,
  onActionChangeFormaPagamento,
  onActionChangeValor,
  onActionChangeInfoGerais,
  onActionViewItem,
  onActionDelete,
}: Props): GridColDef[] => {
  return [
    {
      field: "centroCusto",
      filterable: false,
      headerName: "Centro de Custo",
      width: 125,
      minWidth: 125,
      sortable: false,
    },
    {
      field: "nomeFuncionario",
      filterable: false,
      headerName: "Funcionário",
      width: 125,
      minWidth: 125,
      sortable: false,
    },
    {
      field: "cargo",
      filterable: false,
      headerName: "Cargo",
      width: 150,
      minWidth: 150,
      sortable: false,
    },
    {
      field: "tituloConta",
      filterable: false,
      headerName: "Título",
      width: 150,
      minWidth: 150,
      sortable: false,
      editable: false,
      valueGetter: (_, { idItemFolhaPagamento, tituloConta, enableEdit }) => ({ idItemFolhaPagamento, tituloConta, enableEdit }),
      renderCell: ({ value: { idItemFolhaPagamento, tituloConta, enableEdit } }) => (
        <CustomInput
          value={tituloConta}
          readOnly={!enableEdit}
          onValueChange={e => onActionChangeTitulo(idItemFolhaPagamento, e)} />
      ),
    },
    {
      field: "idSubconta",
      filterable: false,
      headerName: "Subconta",
      flex: 1,
      minWidth: 125,
      sortable: false,
      editable: false,
      valueGetter: (_, { idItemFolhaPagamento, idSubconta, enableEdit }) => ({ idItemFolhaPagamento, idSubconta, enableEdit }),
      renderCell: ({ value: { idItemFolhaPagamento, idSubconta, enableEdit } }) => {
        return (
          <CustomAutocomplete
            value={subcontas.find(x => x.id === idSubconta) ?? null}
            readOnly={!enableEdit}
            options={subcontas}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            onChange={e => onActionChangeSubconta(idItemFolhaPagamento, e?.id ?? null)} />
        );
      },
    },
    {
      field: "formaPagamento",
      filterable: false,
      headerName: "Forma de Pagamento",
      width: 150,
      minWidth: 150,
      sortable: false,
      editable: false,
      valueGetter: (_, { idItemFolhaPagamento, formaPagamento, enableEdit }) => ({ idItemFolhaPagamento, formaPagamento, enableEdit }),
      renderCell: ({ value: { idItemFolhaPagamento, formaPagamento, enableEdit } }) => (
        <CustomInput
          value={formaPagamento ?? null}
          readOnly={!enableEdit}
          onValueChange={e => onActionChangeFormaPagamento(idItemFolhaPagamento, e)} />
      ),
    },
    {
      field: "bancoFuncionario",
      filterable: false,
      headerName: "Banco Pagamento",
      flex: 1,
      minWidth: 125,
      sortable: false,
    },
    {
      field: "valorPagar",
      filterable: false,
      headerName: "Valor R$",
      width: 120,
      minWidth: 120,
      sortable: false,
      editable: false,
      valueGetter: (_, { idItemFolhaPagamento, valorPagar, enableEdit }) => ({ idItemFolhaPagamento, valorPagar, enableEdit }),
      renderCell: ({ value: { idItemFolhaPagamento, valorPagar, enableEdit } }) => (
        <CustomMoneyMaskedInput
          value={valorPagar ?? null}
          readOnly={!enableEdit}
          onChangeValue={e => onActionChangeValor(idItemFolhaPagamento, e)} />
      ),
    },
    {
      field: "observacaoFolha",
      filterable: false,
      headerName: "Informações Gerais",
      flex: 1,
      minWidth: 125,
      sortable: false,
      editable: false,
      valueGetter: (_, { idItemFolhaPagamento, observacaoFolha, status }) => ({ idItemFolhaPagamento, observacaoFolha, status }),
      renderCell: ({ value: { idItemFolhaPagamento, observacaoFolha, status } }) => (
        <CustomInputArea
          value={observacaoFolha ?? null}
          readOnly={status === 1}
          onValueChange={e => onActionChangeInfoGerais(idItemFolhaPagamento, e)} />
      )
    },
    {
      field: "itens",
      filterable: false,
      headerName: "Itens",
      sortable: false,
      valueGetter: (_, { idItemFolhaPagamento }) => ({ idItemFolhaPagamento }),
      renderCell: ({ value: { idItemFolhaPagamento } }) => {
        return (
          <Button
            onMouseDown={e => {
              if (e.button === 1) {
                onActionViewItem(idItemFolhaPagamento, true);
              } else {
                onActionViewItem(idItemFolhaPagamento, e.ctrlKey);
              }
              e.preventDefault();
            }}
            sx={{
              fontSize: 11,
              paddingX: '.4rem',
              paddingY: 0,
              background: '#e6e6e6',
              borderLeft: '.2rem solid #45b6af',
              borderRadius: 0,
              color: 'black',
              fontWeight: 'normal',
            }}>
            Itens
          </Button>
        );
      },
    },
    {
      field: "delete",
      filterable: false,
      headerName: "Excluir",
      width: 70,
      minWidth: 70,
      sortable: false,
      valueGetter: (_, { idItemFolhaPagamento, enableDelete }) => ({ idItemFolhaPagamento, enableDelete }),
      renderCell: ({ value: { idItemFolhaPagamento, enableDelete } }) => {
        return (
          <div className="flex items-start justify-center w-full h-full gap-2">
            {
              enableDelete &&
              <IconButton onClick={() => onActionDelete(idItemFolhaPagamento, true)}>
                <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
              </IconButton>
            }
          </div>
        );
      }
    },
  ];
}